import React from 'react';
import { Autocomplete, Box, InputBase, Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

const RootInput = styled(InputBase)({
  padding: '2px 10px',
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  fontFamily: "'Poppins', sans-serif",
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  fontSize: 13,
});

const OptionRow = styled(Box)(({ selected }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: '6px 10px',
  fontFamily: "'Poppins', sans-serif",
  backgroundColor: selected ? '#EEF0FF' : 'transparent',
}));

export const WebsiteFilterAutocomplete = ({
  list,
  disabled,
  className,
  style,
  value,
  setValue,
  getOptionLabel,
  getOptionDisabled,
  placeholder,
  renderOptionLabel,
  renderOptionEnd,
}) => {
  const { t } = useTranslation();

   const safeList = Array.isArray(list) ? list : [];

  const handleChange = (_event, newValue) => {
    setValue(newValue);
  };

  const resolveLabel = (option) => {
    if (!option) return '';
    if (typeof renderOptionLabel === 'function') {
      return renderOptionLabel(option);
    }
    if (typeof getOptionLabel === 'function') {
      return getOptionLabel(option);
    }
    return '';
  };

  const resolveEnd = (option) =>
    typeof renderOptionEnd === 'function' ? renderOptionEnd(option) : null;

  return (
    <Autocomplete
      options={safeList}
      disabled={disabled}
      value={value ?? null}
      isOptionEqualToValue={(option, val) => option === val}
      noOptionsText={t("Pas d'options")}
      getOptionLabel={(option) => (option ? getOptionLabel(option) : '')}
      getOptionDisabled={(option) =>
        typeof getOptionDisabled === 'function' ? getOptionDisabled(option) : false
      }
      onChange={handleChange}
      fullWidth
      className={className}
      slotProps={{
        paper: {
          sx: { 
            backgroundColor: '#FFFFFF !important',
            border: '1px solid #e0e0e0 !important',
            borderRadius: '8px !important',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1) !important',
            marginTop: '4px !important',
          },
        },
      }}
      ListboxProps={{
        sx: { 
          backgroundColor: '#FFFFFF',
          padding: '4px 0',
          '& li.wf-add-option.Mui-focused, & li.wf-add-option:hover': {
            backgroundColor: 'transparent',
          },
          '& li.MuiAutocomplete-option:not(.wf-add-option):hover': {
            backgroundColor: '#F3F4F6 !important',
            borderRadius: '8px',
          },
        },
      }}
      renderOption={(props, option, state) => {
        const { style, ...rest } = props;

        if (option?.__add__) {
          const addClassName = `${rest.className || ''} wf-add-option`.trim();
          return (
            <li
              {...rest}
              className={addClassName}
              style={{ margin: '6px 8px 8px', listStyle: 'none' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: '#EFEFEA',
                    borderRadius: '10px',
                    padding: '8px 18px',
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#111827',
                  }}
                >
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      marginRight: 4,
                    }}
                  >
                    +
                  </span>
                  <span>{resolveLabel(option)}</span>
                </Box>
              </Box>
            </li>
          );
        }

        const mergedStyle = {
          ...(style || {}),
          backgroundColor: state.selected ? '#EEF0FF' : 'transparent',
          borderRadius: 8,
          margin: '2px 6px',
        };

        return (
          <li {...rest} style={mergedStyle}>
            <OptionRow selected={state.selected}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                {state.selected && (
                  <img
                    src="../images/icon-menu/select-icon.png"
                    alt="selected"
                    style={{
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Tooltip title={resolveLabel(option)} placement="top" arrow>
                  <span
                    style={{
                      fontSize: 13,
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      minWidth: 0,
                    }}
                  >
                    {resolveLabel(option)}
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {resolveEnd(option)}
              </Box>
            </OptionRow>
          </li>
        );
      }}
      renderInput={(params) => {
        const { InputLabelProps, InputProps, ...rest } = params;
        return (
          <RootInput
            {...rest}
            {...InputProps}
            placeholder={placeholder || ''}
            style={style}
          />
        );
      }}
    />
  );
};

export default WebsiteFilterAutocomplete;